import { Column } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Button } from '../button';

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  icon,
  className,
  tooltip,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const align = column.columnDef.meta?.align;

  if (!column.getCanSort()) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
          className
        )}
      >
        {icon && (
          <span className="flex items-center justify-center h-5 w-5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
            {icon}
          </span>
        )}
        {title}
        {tooltip}
      </div>
    );
  }

  return (
    <div
      className={cn(
        align === 'right' && 'flex justify-end',
        align === 'center' && 'flex justify-center'
      )}
    >
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          '-ml-3 h-7 px-2 text-xs font-semibold uppercase tracking-wider',
          'text-gray-600 dark:text-dark-gray-300',
          'hover:bg-gray-200/50 dark:hover:bg-dark-gray-600/50',
          'transition-colors duration-150',
          className
        )}
        onClick={() => column.toggleSorting()}
      >
        {icon && (
          <span className="mr-1.5 flex items-center justify-center h-5 w-5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
            {icon}
          </span>
        )}
        <span>{title}</span>
        {tooltip}
        {column.getIsSorted() === 'desc' ? (
          <ArrowDownIcon className="ml-1.5 h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
        ) : column.getIsSorted() === 'asc' ? (
          <ArrowUpIcon className="ml-1.5 h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
        ) : (
          <ArrowUpDownIcon className="ml-1.5 h-3.5 w-3.5 opacity-50" />
        )}
      </Button>
    </div>
  );
}
